import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../lib/theme";
import { useOrganization } from "../../lib/organizationContext";
import { Badge } from "@heroui/react";
import Bell from "~icons/solar/bell-bold";
import LogOut from "~icons/solar/logout-2-bold";
import SettingsIcon from "~icons/solar/settings-bold";
import Home from "~icons/solar/home-2-bold";
import BarChart3 from "~icons/solar/chart-square-bold";
import Palette from "~icons/solar/palette-bold";
import DollarSign from "~icons/solar/dollar-minimalistic-bold";
import SunIcon from "~icons/solar/sun-2-bold";
import MoonIcon from "~icons/solar/moon-bold";

interface AffiliateHeaderProps {
  notifications?: Array<{
    id: string;
    message: string;
    read: boolean;
    timestamp: string;
  }>;
  onNotificationClick?: () => void;
  showNotifications?: boolean;
  setShowNotifications?: (show: boolean) => void;
}

function NavLink({
  active,
  onClick,
  icon: Icon,
  children,
  className = "",
}: {
  active: boolean;
  onClick: () => void;
  icon: any;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`items-center gap-2 px-3 py-2 rounded-xl transition-colors text-sm font-medium ${
        active
          ? "bg-accent-soft text-accent-soft-foreground"
          : "text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10"
      } ${className}`}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

// expandedWidth is each tab's own required width (pl-3.5 + icon + gap +
// label's natural text width + pr-4, measured for this exact label/font/
// weight) -- not a shared constant. A shared constant sized for the longest
// label ("Analytics") meant every other tab kept visibly growing past the
// point its own icon+label had already fully appeared, reading as a jolt
// tacked onto the end of the animation instead of the animation's own
// natural finish.
const MOBILE_TABS = [
  { path: "/affiliate/dashboard", label: "Overview", icon: Home, expandedWidth: 118 },
  { path: "/affiliate/analytics", label: "Analytics", icon: BarChart3, expandedWidth: 116 },
  { path: "/affiliate/promo-materials", label: "Promo", icon: Palette, expandedWidth: 100 },
  { path: "/affiliate/payments", label: "Payments", icon: DollarSign, expandedWidth: 120 },
];

const COLLAPSED_WIDTH = 48;

// Each page renders its own <AffiliateHeader />, so navigating between pages
// fully unmounts and remounts MobileTabBar -- by the time it mounts, the new
// route is already current, so there's no "old" active tab within this
// component instance to animate away from. Persisting the previously-active
// path across that remount (sessionStorage survives it, component state
// doesn't) and driving the morph as the component's own MOUNT animation
// (initial = old tab's widths, animate = new tab's widths) gives Framer
// Motion a real before/after baked into the first render, instead of
// relying on a later effect/rAF to trigger it -- which is unreliable here
// since the destination page's own data-fetching re-renders can starve rAF
// for a second or more right after mount.
const LAST_TAB_PATH_KEY = "affiliate:lastMobileTabPath";

function isTabActive(tab: { path: string }, activePath: string) {
  return activePath === tab.path || activePath.startsWith(tab.path + "/");
}

function tabTargetWidth(tab: { path: string; expandedWidth: number }, activePath: string) {
  return isTabActive(tab, activePath) ? tab.expandedWidth : COLLAPSED_WIDTH;
}

function MobileTabBar({
  currentPath,
  navigate,
}: {
  currentPath: string;
  navigate: (path: string) => void;
}) {
  const previousPathRef = React.useRef(sessionStorage.getItem(LAST_TAB_PATH_KEY) || currentPath);

  useEffect(() => {
    sessionStorage.setItem(LAST_TAB_PATH_KEY, currentPath);
  }, [currentPath]);

  return (
    <nav
      className="fixed inset-x-4 z-50 flex justify-center md:hidden"
      style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
    >
      <div className="flex items-center gap-1 px-1.5 py-1.5 bg-surface/95 backdrop-blur-md border border-black/10 dark:border-white/10 rounded-full shadow-lg">
        {MOBILE_TABS.map((tab) => {
          const active = isTabActive(tab, currentPath);
          const wasActive = isTabActive(tab, previousPathRef.current);
          const Icon = tab.icon;
          return (
            <motion.button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              aria-label={tab.label}
              aria-current={active ? "page" : undefined}
              initial={{ width: tabTargetWidth(tab, previousPathRef.current) }}
              animate={{ width: tabTargetWidth(tab, currentPath) }}
              transition={{ type: "spring", stiffness: 500, damping: 38 }}
              className={`flex items-center h-11 rounded-full overflow-hidden whitespace-nowrap ${
                active
                  ? "justify-start pl-3.5 pr-4 bg-accent text-accent-foreground"
                  : "justify-center text-muted active:bg-black/5 dark:active:bg-white/10"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {active && (
                <motion.span
                  initial={{ opacity: wasActive ? 1 : 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: 0.08 }}
                  className="ml-1.5 text-sm font-medium whitespace-nowrap"
                >
                  {tab.label}
                </motion.span>
              )}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
}

export default function AffiliateHeader({
  notifications = [],
  showNotifications = false,
  setShowNotifications,
}: AffiliateHeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { data: orgData = null } = useOrganization();

  useEffect(() => {
    // Drives HeroUI's --accent token (Button/Checkbox/links/etc. across
    // every authenticated page render through this shared header), so the
    // whole app reflects this tenant's brand color even when landing
    // directly on an authenticated page without passing through AuthPage
    // first.
    if (orgData?.branding?.primary_color) {
      document.documentElement.style.setProperty("--accent", orgData.branding.primary_color);
    }
  }, [orgData]);

  const appName = orgData?.app_name || "Affiliate Hub";
  const logoUrl = orgData?.branding?.logo_url;

  const unreadCount = notifications.filter((n) => !n.read).length;

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
    <header className="bg-surface border-b border-black/10 dark:border-white/10 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/affiliate/dashboard")}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={`${appName} logo`}
                className="h-9 w-auto object-contain"
              />
            ) : (
              <img
                src={theme === "dark" ? "/icon-dark.svg" : "/icon-light.svg"}
                alt={appName}
                className="w-9 h-9 rounded-xl shrink-0"
              />
            )}
            <div className="hidden sm:block leading-tight">
              <p className="text-[11px] text-muted">Affiliate ID</p>
              <p className="text-sm font-medium text-foreground font-mono">{user?.affiliateCode}</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Navigation Links */}
            <NavLink
              className="hidden md:inline-flex"
              active={isActiveRoute("/affiliate/dashboard")}
              onClick={() => navigate("/affiliate/dashboard")}
              icon={Home}
            >
              Overview
            </NavLink>

            <NavLink
              className="hidden md:inline-flex"
              active={isActiveRoute("/affiliate/analytics")}
              onClick={() => navigate("/affiliate/analytics")}
              icon={BarChart3}
            >
              Analytics
            </NavLink>

            <NavLink
              className="hidden md:inline-flex"
              active={isActiveRoute("/affiliate/promo-materials")}
              onClick={() => navigate("/affiliate/promo-materials")}
              icon={Palette}
            >
              Promo
            </NavLink>

            <NavLink
              className="hidden sm:inline-flex"
              active={isActiveRoute("/affiliate/payments")}
              onClick={() => navigate("/affiliate/payments")}
              icon={DollarSign}
            >
              Payments
            </NavLink>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-xl transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>

            {/* Notifications */}
            {setShowNotifications && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-xl transition-colors"
                >
                  {unreadCount > 0 ? (
                    <Badge.Anchor>
                      <Bell className="h-5 w-5" />
                      <Badge color="danger" size="sm">{unreadCount}</Badge>
                    </Badge.Anchor>
                  ) : (
                    <Bell className="h-5 w-5" />
                  )}
                </button>
              </div>
            )}

            {/* Settings */}
            <button
              onClick={() => navigate("/affiliate/settings")}
              className="p-2 text-muted hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 rounded-xl transition-colors"
            >
              <SettingsIcon className="h-5 w-5" />
            </button>

            {/* Logout */}
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 text-muted hover:text-danger hover:bg-danger-soft rounded-xl transition-colors text-sm font-medium"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
    <MobileTabBar currentPath={location.pathname} navigate={navigate} />
    </>
  );
}
